import { useEffect, useMemo, useState, type ReactNode } from 'react';
import Link from '@/components/compat/Link';
import { getCountdown, getLatestPower } from '@/services/xat-proxy.service';
import {
  ArrowRight,
  Loader2,
  Calendar,
  Store,
  Tag,
} from 'lucide-react';
import {
  ImageBackdropLayers,
  cn,
  GsImage,
  MoXat,
  resolvePowerCategoryDisplay,
  type LatestPower,
} from '@/components/help-center/latest-power-shared';
import { PortalCrystalSeparator } from '@/components/help-center/PortalCrystalSeparator';
import { PowerCtaArrow } from '@/components/help-center/powers/PowerCtaArrow';
import { formatPowerDisplayName } from '@/lib/power-mention-data';
import { getPowersDetailedStats } from '@/lib/powers-data';
import { useCatalogPowers } from '@/components/painel/powers/powers-cms-context';

type CountdownApi = {
  code?: number;
  status?: string;
  status_code?: string;
  power_info?: { id?: number; name?: string; type?: string } | null;
  [key: string]: unknown;
};

function extractCountdownEndMs(data: CountdownApi | null): number | null {
  if (!data) return null;
  if (data.status_code === 'no_countdown') return null;

  const tryUnix = (v: unknown): number | null => {
    if (typeof v === 'number') {
      if (v > 1e12) return v;
      if (v > 1e9 && v < 1e12) return v * 1000;
    }
    if (typeof v === 'string' && /^\d+$/.test(v)) {
      const n = parseInt(v, 10);
      if (n > 1e12) return n;
      if (n > 1e9) return n * 1000;
    }
    return null;
  };

  const keys = ['countdown_end', 'end_time', 'end', 'ends_at', 'release_time', 'unix', 'timestamp'];
  for (const k of keys) {
    const ms = tryUnix(data[k]);
    if (ms && ms > Date.now() - 60_000) return ms;
  }

  const nested = data.countdown;
  if (nested && typeof nested === 'object') {
    const o = nested as Record<string, unknown>;
    for (const k of keys) {
      const ms = tryUnix(o[k]);
      if (ms && ms > Date.now() - 60_000) return ms;
    }
  }

  const secKeys = ['remaining_int', 'seconds', 'seconds_left', 'time_left', 'remaining', 'secs'];
  for (const k of secKeys) {
    const v = data[k];
    const s = typeof v === 'number' ? v : typeof v === 'string' ? parseInt(String(v), 10) : NaN;
    if (!Number.isNaN(s) && s > 0 && s < 86400 * 365) {
      return Date.now() + s * 1000;
    }
  }

  return null;
}

function splitRemaining(ms: number) {
  const s = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  return { days, hours, minutes, seconds };
}

function pad2(n: number) {
  return n < 10 ? `0${n}` : String(n);
}

/** Card de contagem — mesmas cores das info rows (labels) para consistência. */
const countdownCardClass = cn(
  'w-full max-w-[min(100%,420px)] rounded-2xl px-3.5 py-2.5 text-center',
  'border border-white/[0.08] bg-gradient-to-br from-white/[0.07] to-white/[0.02]',
  'shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] backdrop-blur-sm'
);

function formatHhMmSsFromRemaining(ms: number) {
  const { days, hours, minutes, seconds } = splitRemaining(ms);
  const totalHours = days * 24 + hours;
  const hPart = totalHours < 100 ? pad2(totalHours) : String(totalHours);
  return `${hPart}:${pad2(minutes)}:${pad2(seconds)}`;
}

function CountdownWidget() {
  const [data, setData] = useState<CountdownApi | null>(null);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const j = await getCountdown();
        if (!cancelled) setData(j as CountdownApi);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    const poll = setInterval(load, 60_000);
    return () => {
      cancelled = true;
      clearInterval(poll);
    };
  }, []);

  // Ancorar o fim da contagem apenas quando os dados mudam. Se a API devolver
  // segundos restantes (valor relativo), recalcular a cada render faria o tempo
  // "andar junto" com `now` e a contagem ficaria congelada.
  const endMs = useMemo(() => (data ? extractCountdownEndMs(data) : null), [data]);
  const remaining = endMs != null ? Math.max(0, endMs - now) : null;
  const active = remaining != null && remaining > 0;

  // Só mostra quando existe lançamento programado com tempo restante.
  if (loading || !active || remaining == null) return null;

  const timeStr = formatHhMmSsFromRemaining(remaining);

  return (
    <div
      className={cn(countdownCardClass, 'mt-8 flex flex-col items-center gap-1.5 py-3.5')}
      title="Contagem para o próximo lançamento"
      role="timer"
      aria-live="polite"
    >
      <span className="font-poppins text-[9px] font-semibold uppercase tracking-[0.15em] text-[#b8c9ea] sm:text-[10px] sm:tracking-[0.18em]">
        O PRÓXIMO LANÇAMENTO SERÁ EM
      </span>
      <span
        className="font-poppins text-xl font-bold tabular-nums leading-none tracking-tight text-white sm:text-2xl"
        style={{
          textShadow:
            '0 0 22px rgba(255,255,255,0.4), 0 0 10px rgba(186,210,255,0.5), 0 1px 3px rgba(0,0,0,0.35)',
        }}
      >
        {timeStr}
      </span>
    </div>
  );
}

const BENEFITS: { smiley: string; title: string; desc: string; sizeClass: string }[] = [
  {
    smiley: 'pstyle',
    title: 'Personalização',
    desc: 'Customize seu nome, perfil e experiência.',
    sizeClass: 'h-14 w-14',
  },
  {
    smiley: 'gline',
    title: 'Smilies exclusivos',
    desc: 'Colecione centenas de smilies e animações.',
    sizeClass: 'h-10 w-10',
  },
  {
    smiley: 'lights2',
    title: 'Recursos especiais',
    desc: 'Desbloqueie funcionalidades exclusivas do xat.',
    sizeClass: 'h-16 w-16',
  },
  {
    smiley: 'supersummer',
    title: 'Coleções',
    desc: 'Explore centenas de powers lançados ao longo dos anos.',
    sizeClass: 'h-14 w-14',
  },
];

const POWERPEDIA_HOME_STATS = [
  {
    iconSrc: '/icons/power.svg',
    label: 'Powers',
    valueKey: 'totalPowers' as const,
    color: 'text-sky-300',
    bg: 'bg-sky-400/10',
  },
  {
    iconSrc: '/icons/smile.svg',
    label: 'Smilies',
    valueKey: 'totalSmilies' as const,
    color: 'text-amber-300',
    bg: 'bg-amber-400/10',
  },
  {
    iconSrc: '/icons/pawn.svg',
    label: 'Peões',
    valueKey: 'totalPawns' as const,
    color: 'text-violet-300',
    bg: 'bg-violet-400/10',
  },
  {
    iconSrc: '/icons/grouppowers.svg',
    label: 'Group Powers',
    valueKey: 'totalGroups' as const,
    color: 'text-emerald-300',
    bg: 'bg-emerald-400/10',
  },
];

/** Ícone SVG pintado com currentColor (mesmo padrão do hero /powers). */
function PowerpediaStatIcon({ src, className }: { src: string; className?: string }) {
  return (
    <span
      className={cn('inline-block bg-current', className)}
      style={{
        maskImage: `url(${src})`,
        maskRepeat: 'no-repeat',
        maskPosition: 'center',
        maskSize: 'contain',
        WebkitMaskImage: `url(${src})`,
        WebkitMaskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
        WebkitMaskSize: 'contain',
      }}
      aria-hidden
    />
  );
}

const powerInfoRowClass = cn(
  'flex w-full min-h-[42px] items-center justify-between gap-3 rounded-2xl px-3.5 py-2.5',
  'border border-white/[0.08] bg-gradient-to-br from-white/[0.07] to-white/[0.02]',
  'shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] backdrop-blur-sm',
  'transition-colors duration-200 hover:border-sky-400/25 hover:from-white/[0.09]'
);

function ValueWithMo({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex min-w-0 items-center justify-end gap-1.5 font-poppins text-xs font-semibold text-blue-50 sm:text-sm">
      <MoXat />
      <span>{children}</span>
    </span>
  );
}


export default function PowersSection() {
  const { powers: catalogPowers, hydrated: catalogHydrated } = useCatalogPowers();
  const powerpediaStats = useMemo(
    () => getPowersDetailedStats(catalogHydrated ? catalogPowers : undefined),
    [catalogHydrated, catalogPowers]
  );
  const [power, setPower] = useState<LatestPower | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getLatestPower();
        const first = data.powers?.[0];
        if (!cancelled) {
          if (first) setPower(first);
          else setError('Nenhum power encontrado');
        }
      } catch {
        if (!cancelled) setError('Não foi possível carregar o último power.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const isLimited = power && (power.status === 'limited' || power.status === 'Limited');
  const powerDisplayName = power ? formatPowerDisplayName(power.name) : '';
  const powerHref = useMemo(() => {
    if (!power) return null;
    const nameKey = power.name.trim().toLowerCase();
    const fromCatalog = catalogPowers.find(
      (p) =>
        p.id === power.id ||
        p.slug === nameKey ||
        p.name.trim().toLowerCase() === nameKey
    );
    const slug = fromCatalog?.slug || nameKey;
    return `/powers/${encodeURIComponent(slug)}`;
  }, [power, catalogPowers]);

  return (
    <section className="relative overflow-hidden py-20 px-6">
      <ImageBackdropLayers className="absolute inset-0" />
      <div className="pointer-events-none absolute inset-0 bg-slate-950/55" aria-hidden />

      <div className="relative z-10 mx-auto max-w-7xl">
        <div className="flex flex-col-reverse gap-10 lg:flex-row lg:items-stretch lg:gap-12">
          {/* Marketing — no mobile fica abaixo do card do último power */}
          <div className="flex min-w-0 flex-col text-white lg:flex-1">
            {/* Badge */}
            <span className="mb-6 inline-flex w-fit items-center gap-2 rounded-full border border-sky-400/25 bg-sky-500/[0.08] px-3.5 py-1.5 font-poppins text-[11px] font-semibold uppercase tracking-wide text-sky-200 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] backdrop-blur-sm">
              <PowerpediaStatIcon src="/icons/power.svg" className="h-3.5 w-3.5 shrink-0 text-sky-300" />
              Powerpedia
            </span>

            {/* Headline */}
            <div className="mb-5 flex items-center gap-2 sm:gap-2.5">
              <img
                src="/icons/powerpediabook.png"
                alt=""
                className="-mr-1 ml-[5px] h-28 w-auto shrink-0 object-contain drop-shadow-[0_10px_24px_rgba(56,189,248,0.28)] sm:-mr-2 sm:h-32 md:h-36"
                decoding="async"
                draggable={false}
                aria-hidden
              />
              <h2 className="min-w-0 font-poppins text-[clamp(1.9rem,4vw,3rem)] font-bold leading-[1.1] tracking-tight text-white drop-shadow-sm">
                Explore o universo dos{' '}
                <span className="powers-title-animated-gradient bg-clip-text text-transparent">
                  powers do xat
                </span>
              </h2>
            </div>

            {/* Texto */}
            <p className="mb-9 w-full text-pretty font-poppins text-[15px] leading-[1.7] text-blue-100/80 md:text-base md:leading-[1.75]">
              Os powers transformam completamente sua experiência no xat. Descubra recursos exclusivos,
              efeitos visuais, smilies, stickers, coleções e centenas de possibilidades para personalizar
              seu chat e seu perfil.
            </p>

            {/* Cards de benefícios — grid 2x2 */}
            <div className="mb-9 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {BENEFITS.map(({ smiley, title, desc, sizeClass }) => (
                <div
                  key={title}
                  className={cn(
                    'group rounded-2xl border border-white/[0.08] bg-gradient-to-br from-white/[0.06] to-white/[0.015] px-3.5 py-3',
                    'shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] backdrop-blur-sm',
                    'transition-all duration-200 hover:border-sky-400/30 hover:from-white/[0.09]'
                  )}
                >
                  <div className="mb-2 flex h-16 items-center">
                    <GsImage
                      code={smiley}
                      className={cn('flex shrink-0 items-center justify-center', sizeClass)}
                      imgClassName="h-full w-full max-h-full max-w-full object-contain"
                      sizePx={40}
                    />
                  </div>
                  <h3 className="font-poppins text-[14px] font-semibold tracking-tight text-white">
                    {title}
                  </h3>
                  <p className="mt-0.5 font-poppins text-[12.5px] leading-snug text-blue-100/70">
                    {desc}
                  </p>
                </div>
              ))}
            </div>

            {/* Destaque estatístico — mesmo estilo do hero /powers */}
            <div
              className={cn(
                'mb-9 overflow-hidden rounded-2xl border border-white/[0.08] bg-gradient-to-br from-white/[0.06] to-white/[0.015]',
                'shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] backdrop-blur-sm'
              )}
            >
              <div className="relative grid grid-cols-2 sm:grid-cols-4">
                {POWERPEDIA_HOME_STATS.map(({ iconSrc, label, valueKey, color, bg }, index) => (
                  <div
                    key={label}
                    className={cn(
                      'relative flex flex-col items-center gap-1.5 px-3 py-4',
                      index % 2 === 1 && 'border-l border-white/[0.08]',
                      index >= 2 && 'border-t border-white/[0.08] sm:border-t-0',
                      index > 0 && 'sm:border-l sm:border-white/[0.08]'
                    )}
                  >
                    {index > 0 ? (
                      <span
                        className="pointer-events-none absolute -left-px top-1/2 hidden h-14 w-px -translate-y-1/2 bg-gradient-to-b from-transparent via-white/[0.14] to-transparent sm:block"
                        aria-hidden
                      />
                    ) : null}

                    <div
                      className={cn(
                        'flex h-8 w-8 items-center justify-center rounded-lg ring-1 ring-white/10',
                        bg
                      )}
                    >
                      <PowerpediaStatIcon src={iconSrc} className={cn('h-5 w-5', color)} />
                    </div>
                    <span className={cn('font-poppins text-xl font-bold leading-none tabular-nums', color)}>
                      {powerpediaStats[valueKey].toLocaleString('pt-BR')}
                    </span>
                    <span className="text-center font-poppins text-[10px] font-medium uppercase tracking-wide text-sky-200/70">
                      {label}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA principal */}
            <div className="flex w-full flex-row items-stretch gap-3">
              <Link
                href="/powers"
                className={cn(
                  'group relative flex min-w-0 flex-1 items-center justify-center gap-2 overflow-hidden rounded-2xl px-4 py-4 sm:px-6',
                  'bg-gradient-to-r from-sky-500 via-sky-400 to-cyan-400 font-poppins text-[13px] font-bold text-white sm:text-[15px]',
                  'shadow-[0_0_0_1px_rgba(56,189,248,0.45),0_14px_44px_-6px_rgba(14,165,233,0.65)]',
                  'ring-1 ring-sky-300/30',
                  'transition-all duration-200 hover:brightness-110 hover:shadow-[0_0_0_1px_rgba(125,211,252,0.6),0_18px_52px_-4px_rgba(14,165,233,0.75)]',
                  'active:scale-[0.99]'
                )}
              >
                <span
                  className="pointer-events-none absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 opacity-0 transition-opacity duration-200 group-hover:opacity-100"
                  aria-hidden
                />
                <span className="relative whitespace-nowrap">Explorar Powerpedia</span>
                <ArrowRight
                  className="relative h-4 w-4 shrink-0 sm:h-5 sm:w-5 transition-transform duration-200 group-hover:translate-x-1"
                  strokeWidth={2.75}
                />
              </Link>
            </div>
          </div>

          {/* Separador vertical entre as colunas */}
          <div className="hidden shrink-0 lg:flex lg:flex-col lg:items-center" aria-hidden>
            <span className="w-px flex-1 bg-gradient-to-b from-transparent to-sky-400/40" />
            <span className="my-3 h-2 w-2 rotate-45 rounded-[3px] bg-sky-400/70 shadow-[0_0_12px_rgba(56,189,248,0.65)]" />
            <span className="w-px flex-1 bg-gradient-to-b from-sky-400/40 to-transparent" />
          </div>

          {/* Último power */}
          <div className="relative mx-auto flex w-full max-w-[420px] flex-col justify-center lg:mx-0">
            {loading ? (
              <div className="flex min-h-[200px] flex-col items-center justify-center gap-3 rounded-2xl border border-blue-900/50 bg-blue-950/40 p-8 ring-1 ring-blue-950/40 backdrop-blur-md">
                <Loader2 className="h-8 w-8 animate-spin text-sky-300" />
                <p className="text-xs text-sky-100/90 font-poppins">Carregando último power…</p>
              </div>
            ) : error || !power ? (
              <div className="flex min-h-[180px] flex-col items-center justify-center rounded-2xl border border-dashed border-blue-800/50 bg-blue-950/30 p-8 text-center ring-1 ring-blue-950/30">
                <p className="text-sm text-sky-200/90 font-poppins">{error ?? 'Indisponível no momento.'}</p>
              </div>
            ) : (
              <>
                <div
                  className={cn(
                    'relative overflow-hidden rounded-2xl border border-blue-900/60 bg-blue-950/30 shadow-xl shadow-black/35 ring-1 ring-sky-500/25',
                    'transition-colors duration-300 hover:border-blue-800/70'
                  )}
                >
                  <ImageBackdropLayers className="absolute inset-0 overflow-hidden rounded-2xl" />
                  <div className="pointer-events-none absolute inset-0 bg-slate-950/72" aria-hidden />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#030023]/45 via-transparent to-[#030023]/80" aria-hidden />

                  <div className="relative z-10 flex flex-col px-5 pb-5 pt-5">

                    {/* Row 2: ícone centralizado + ID sobreposto + nome + badge de status */}
                    <div className="mb-5 flex flex-col items-center gap-3">
                      {/* Ícone com badge ID sobreposto no canto superior direito */}
                      <div className="relative">
                        <div
                          className="relative shrink-0"
                          aria-hidden={false}
                        >
                          {/* Caixa do ícone: gradient radial orbital (claro no centro, escuro nas bordas) */}
                          <div
                            className="flex h-24 w-24 items-center justify-center rounded-2xl"
                            style={{
                              background: 'radial-gradient(circle at 50% 48%, #112268 0%, #0a1450 42%, #060c38 75%, #030719 100%)',
                              boxShadow: '0 0 0 1.5px #0b1658, 0 0 0 2.5px #0f1e70, 8px 12px 28px rgba(0,0,0,0.55)',
                            }}
                          >
                            <GsImage
                              code={power.name}
                              className="flex h-full w-full items-center justify-center"
                              imgClassName="max-h-[76px] max-w-[76px] object-contain"
                            />
                          </div>
                        </div>
                        {/* Badge ID: tons de azul escuro */}
                        <span
                          className="pointer-events-none absolute -top-2.5 -right-3 inline-flex cursor-default items-center rounded-full px-2.5 py-0.5 font-poppins text-[11px] font-bold tabular-nums shadow-sm"
                          style={{
                            background: 'linear-gradient(135deg, #0e1f7a 0%, #091460 100%)',
                            color: '#8aabf8',
                            boxShadow: '0 0 0 1.5px #1a3099',
                          }}
                          title="ID do power"
                        >
                          ID {power.id}
                        </span>
                      </div>

                      {/* Nome do power: fonte grande com glow branco suave */}
                      <span
                        className="font-poppins text-[2.35rem] font-semibold tracking-tight text-white leading-none"
                        style={{ textShadow: '0 0 28px rgba(255,255,255,0.32), 0 0 8px rgba(255,255,255,0.14)' }}
                      >
                        {powerDisplayName}
                      </span>

                      {/* Badge de status */}
                      {isLimited ? (
                        <span
                          className="inline-flex items-center rounded-full px-5 py-1.5 font-poppins text-[11px] font-bold uppercase tracking-widest text-white"
                          style={{
                            background: 'linear-gradient(90deg, #e8620a 0%, #f47c1a 50%, #e8a020 100%)',
                            boxShadow: '0 0 0 1.5px rgba(240,130,30,0.40), 0 4px 14px rgba(220,90,10,0.35)',
                          }}
                        >
                          Limitado
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-gradient-to-r from-cyan-500/30 to-sky-500/30 px-5 py-1.5 font-poppins text-[11px] font-bold uppercase tracking-widest text-cyan-300 ring-1 ring-cyan-400/35">
                          Ilimitado
                        </span>
                      )}
                    </div>

                    {/* Info rows */}
                    <div className="space-y-2">
                      <div className={powerInfoRowClass}>
                        <span className="flex min-w-0 items-center gap-2 font-poppins text-[10px] font-semibold uppercase tracking-wide text-sky-200/75">
                          <Calendar className="h-3.5 w-3.5 shrink-0 text-sky-300/90" aria-hidden />
                          Data de lançamento:
                        </span>
                        <span className="min-w-0 text-right font-poppins text-xs font-semibold text-blue-50 sm:text-sm">
                          {power.released}
                        </span>
                      </div>
                      <div className={powerInfoRowClass}>
                        <span className="flex min-w-0 items-center gap-2 font-poppins text-[10px] font-semibold uppercase tracking-wide text-sky-200/75">
                          <Tag className="h-3.5 w-3.5 shrink-0 text-sky-300/90" aria-hidden />
                          Categoria:
                        </span>
                        <span className="min-w-0 text-right font-poppins text-xs font-semibold text-blue-50 sm:text-sm" title={resolvePowerCategoryDisplay(power)}>
                          {resolvePowerCategoryDisplay(power)}
                        </span>
                      </div>
                      <div className={powerInfoRowClass}>
                        <span className="flex min-w-0 items-center gap-2 font-poppins text-[10px] font-semibold uppercase tracking-wide text-sky-200/75">
                          <Store className="h-3.5 w-3.5 shrink-0 text-sky-300/90" aria-hidden />
                          Preço (loja):
                        </span>
                        <ValueWithMo>{power.prices.store}</ValueWithMo>
                      </div>
                      <div className={powerInfoRowClass}>
                        <span className="flex min-w-0 items-center gap-2 font-poppins text-[10px] font-semibold uppercase tracking-wide text-sky-200/75">
                          <PowerpediaStatIcon
                            src="/icons/tradeicon2.svg"
                            className="h-3.5 w-3.5 shrink-0 text-sky-300/90"
                          />
                          Fairtrade:
                        </span>
                        <ValueWithMo>
                          {power.prices.fairtrade_min}–{power.prices.fairtrade_max} xats
                        </ValueWithMo>
                      </div>
                    </div>

                    {powerHref ? (
                      <>
                        <PortalCrystalSeparator
                          className="my-5"
                          accentRgb="56, 189, 248"
                        />
                        <Link
                          href={powerHref}
                          className={cn(
                            'group relative inline-flex w-full items-center justify-center gap-2.5 overflow-hidden rounded-2xl px-5 py-3.5',
                            'bg-white/[0.07] ring-1 ring-white/[0.12]',
                            'font-poppins text-sm font-semibold text-white',
                            'transition-all duration-200 ease-out',
                            'hover:bg-white/[0.12] hover:ring-white/20 hover:text-sky-100',
                            'active:scale-[0.98] active:bg-white/[0.09]'
                          )}
                        >
                          <span>Ver power</span>
                          <PowerCtaArrow />
                        </Link>
                      </>
                    ) : null}

                    <CountdownWidget />
                  </div>
              </div>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
